import clsx from 'clsx'
import React from 'react'

import {SvgIcon, SvgIconProps} from './SvgIcon'
import styleClasses from './utils.module.css'

type CreateLargeSvgIconOptions = {
  className?: string
  viewBox?: string
}

export const createLargeSvgIcon = (
  children: React.ReactNode,
  displayName: string,
  options?: CreateLargeSvgIconOptions,
): typeof SvgIcon => {
  const Component = (props: SvgIconProps, ref: React.Ref<SVGSVGElement>) => (
    <SvgIcon
      ref={ref}
      classes={{root: styleClasses.large}}
      viewBox={options?.viewBox || '0 0 40 40'}
      {...props}
      className={clsx(options?.className, props.className)}
    >
      {children}
    </SvgIcon>
  )

  if (process.env.NODE_ENV !== 'production') {
    // Need to set `displayName` on the inner component for React.memo.
    // React prior to 16.14 ignores `displayName` on the wrapper.
    Component.displayName = `${displayName}Logo`
  }

  return React.memo(React.forwardRef(Component))
}

type CreateSvgIconOptions = {
  className?: string
  viewBox?: string
  /**
   * Флаг для отзеркаливания иконки в rtl версии
   */
  rtlTransform?: boolean
}

export const createSvgIcon = (
  path: React.ReactElement,
  displayName: string,
  options?: CreateSvgIconOptions,
): typeof SvgIcon => {
  const Component = (props: SvgIconProps, ref: React.Ref<SVGSVGElement>) => {
    const svgProps = {
      ref,
      ...props,
      className: clsx(options?.rtlTransform && styleClasses.rtlTransform, options?.className, props.className),
      ...(options?.viewBox && {
        viewBox: options.viewBox,
      }),
    }

    return React.createElement(SvgIcon, svgProps, path)
  }

  if (process.env.NODE_ENV !== 'production') {
    // Need to set `displayName` on the inner component for React.memo.
    // React prior to 16.14 ignores `displayName` on the wrapper.
    Component.displayName = `${displayName}Icon`
  }

  return React.memo(React.forwardRef(Component))
}

export type IconType = ReturnType<typeof createSvgIcon>
